import type { PointerEvent as ReactPointerEvent } from 'react'
import { Coffee, UserMinus } from 'lucide-react'
import type { BoardSpan } from '../../../shared/lib/schedule'
import {
  STRIP_ICON_MIN,
  distributeSegmentHeights,
  resolveWorkDensity,
  type WorkDensity,
} from '../model/blockDensity'
import type { BarBlockView, BlockView, JobBlockView } from '../model/blockView'
import { MIN_BLOCK_PX, spanHeightPx, spanTopPx } from '../model/calendarConstants'
import { BlockSegment, META_CLASS, SUBJECT_CLASS } from './BlockSegment'
import { CalendarBlockShell } from './CalendarBlockShell'
import { TravelStrip } from './TravelStrip'
import { WorkBody } from './WorkBody'

/**
 * One entry on the worker calendar — Termin, Aufgabe, Pause and Abwesenheit all render through
 * here, the way the board's `ScheduleBlock` does. What differs between the kinds is data, and
 * `resolveBlockView` has already decided it; this component only knows two shapes: a job, which is
 * three strips, and a bar, which is one.
 *
 * Interaction is not decided here either. `DayColumn` owns the policy (who may drag what) and
 * hands down the handlers; the shell turns their presence into a button or a drag target.
 */

interface Props {
  view: BlockView
  /** The occupied block from `schedule.startsAt`/`endsAt` — see `buildDayBlocks`. */
  span: BoardSpan
  left?: string
  zIndex?: number
  /** Tap, click or Enter. Absent on a block that opens nothing. */
  onActivate?: () => void
  onPointerDown?: (e: ReactPointerEvent<HTMLDivElement>) => void
  isDragging?: boolean
  /** Where a drag has moved the block to, in grid pixels — replaces the span's own top. */
  overrideTopPx?: number
}

export const WorkerScheduleBlock = ({
  view,
  span,
  left,
  zIndex,
  onActivate,
  onPointerDown,
  isDragging,
  overrideTopPx,
}: Props) => {
  const top = overrideTopPx ?? spanTopPx(span)
  const height = spanHeightPx(span, MIN_BLOCK_PX)

  const label = view.layout === 'job' ? `${view.kindLabel} ${view.description}` : view.description

  return (
    <CalendarBlockShell
      topPx={top}
      heightPx={height}
      left={left}
      zIndex={zIndex}
      label={label}
      title={view.description}
      onActivate={onActivate}
      onPointerDown={onPointerDown}
      isDragging={isDragging}
      // Pause and Abwesenheit recede: they are the day's background, not the day's work.
      muted={view.layout === 'bar'}
    >
      {view.layout === 'job' ? (
        <JobStrips view={view} heightPx={height} />
      ) : (
        <BarBody view={view} heightPx={height} />
      )}
    </CalendarBlockShell>
  )
}

/** Anfahrt / Arbeit / Abfahrt, sized against the box so they can never add up to more than it. */
const JobStrips = ({ view, heightPx }: { view: JobBlockView; heightPx: number }) => {
  const [travelPx, workPx, departurePx] = distributeSegmentHeights(heightPx, view.minutes)
  const fit = resolveWorkDensity(workPx)

  return (
    <>
      <TravelStrip kind={view.travelKind} color={view.travelColor} heightPx={travelPx} />

      <BlockSegment
        color={view.workColor}
        heightPx={workPx}
        divided={travelPx > 0}
        ringed={view.ringed}
      >
        {/* `null`, not an element that renders nothing: a childless segment skips its scrim, and a
            block too short for text should keep its full colour rather than a darkened one. */}
        {fit.tier === 'bare' ? null : (
          <WorkBody
            fit={fit}
            timeLabel={view.timeLabel}
            subject={view.subject}
            who={view.who}
            contact={view.contact}
            running={view.running}
            attachmentCount={view.attachmentCount}
          />
        )}
      </BlockSegment>

      <TravelStrip
        kind={view.departureKind}
        color={view.departureColor}
        heightPx={departurePx}
        divided
      />
    </>
  )
}

const BAR_ICON = { coffee: Coffee, 'user-minus': UserMinus } as const

/**
 * One strip with an icon — the same ladder as a job's work segment. A short Pause keeps its cup
 * where it cannot keep the word, so the grid stays readable without the worker decoding grey.
 */
const BarBody = ({ view, heightPx }: { view: BarBlockView; heightPx: number }) => {
  const { tier } = resolveWorkDensity(heightPx)
  const Icon = BAR_ICON[view.icon]
  const icon = <Icon className="h-3 w-3 shrink-0" strokeWidth={2.5} aria-hidden />

  return (
    <BlockSegment color={view.color} heightPx={Math.round(heightPx)} padded={tier !== 'bare'}>
      {resolveBarContent(tier, heightPx, icon, view)}
    </BlockSegment>
  )
}

function resolveBarContent(
  tier: WorkDensity,
  heightPx: number,
  icon: JSX.Element,
  view: BarBlockView,
) {
  if (tier === 'bare') return heightPx >= STRIP_ICON_MIN ? icon : null

  // The meta rides on the label's row when there is only one, and takes its own below that.
  if (tier === 'single' || view.meta == null) {
    return (
      <span className="flex min-w-0 items-center gap-1">
        {icon}
        <span className={SUBJECT_CLASS}>{view.label}</span>
        {view.meta && (
          <span className={`${META_CLASS} ml-auto shrink-0 tabular-nums`}>{view.meta}</span>
        )}
      </span>
    )
  }

  return (
    <div className="flex h-full w-full min-w-0 flex-col justify-start">
      <span className="flex min-w-0 items-center gap-1">
        {icon}
        <span className={SUBJECT_CLASS}>{view.label}</span>
      </span>
      <span className={`${META_CLASS} tabular-nums`}>{view.meta}</span>
    </div>
  )
}
